/**
 * Security utilities for cryptographic operations and secure comparisons.
 *
 * @see docs/SECURITY_COMPLIANCE.md - Requirement #45: Constant-Time Cryptographic Operations
 */

import crypto from 'crypto';

/**
 * Perform timing-safe comparison of two strings to prevent timing attacks.
 * Converts strings to Buffers and uses crypto.timingSafeEqual() for constant-time comparison.
 *
 * This function prevents timing attacks by ensuring the comparison takes constant time
 * regardless of where the strings differ. This is critical for comparing secrets, tokens,
 * and other sensitive values.
 *
 * @see docs/SECURITY_COMPLIANCE.md - Requirement #45: Constant-Time Cryptographic Operations
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns true if strings match, false otherwise
 *
 * @example
 * ```ts
 * const secret = process.env.WEBHOOK_SECRET;
 * const token = request.headers.get('x-webhook-token');
 *
 * if (!timingSafeCompare(token, secret)) {
 *   return new Response('Unauthorized', { status: 403 });
 * }
 * ```
 */
export function timingSafeCompare(a: string, b: string): boolean {
  const bufferA = Buffer.from(a, 'utf8');
  const bufferB = Buffer.from(b, 'utf8');

  // If lengths differ, strings don't match
  // Length comparison is safe (not a timing attack vector)
  if (bufferA.length !== bufferB.length) {
    return false;
  }

  // Perform timing-safe comparison (requires buffers of same length)
  return crypto.timingSafeEqual(bufferA, bufferB);
}
